import React, { useState, useEffect } from "react";
import { Box, Heading, Text, useThemeUI } from "theme-ui";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash/fp";

import * as styles from "./ProductsSelectorV2.styles";
import { fetchRecipe } from "store/app/appSlice";
import {
  ProductBreakdownSelector,
  Button,
  ProductButtonV2,
  Input,
  Header,
  SvgOrImg,
} from "components";
import { ArrowRightIcon } from "components/icons";
import { useTranslation } from "hooks";
import { isBlockedGoalieGearRecipe } from "utils/constants";

export default function ProductsSelectorV2({
  title,
  appDivider,
  uiText: {
    selectProductSubHeading,
    selectProductCopy1,
    selectProductCopy2,
    selectProductDisclaimer,
    selectProductButtonsHeader,
    selectProductButtonsSubHeader,
    link_1,
  },
  appHeaderLogo,
  appLogo,
  products = [],
  selectedProduct,
  selectProduct,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    theme: { isDesktop },
  } = useThemeUI();

  const [showProductBreakdown, setShowProductBreakdown] = useState(false);

  const [recipeID, setRecipeID] = useState("");
  const [recipeIDError, setRecipeIDError] = useState(null);

  const isRestrictedModeEnabled = useSelector(
    get(["app", "formData", "restrictedModeEnabled"])
  );

  useEffect(() => {
    setRecipeIDError(null);
    let promise = { abort: () => {} };

    if (recipeID) {
      if (isBlockedGoalieGearRecipe(recipeID)) {
        setRecipeIDError(
          t("This recipe is no longer supported. Please create a new design.")
        );
      } else {
        promise = dispatch(fetchRecipe({ recipeID }));
        promise.unwrap().catch((error) => {
          console.log(error);
          setRecipeIDError(error.message);
        });
      }
    }

    return () => {
      promise.abort();
    };
  }, [recipeID]);

  if (showProductBreakdown) {
    return (
      <ProductBreakdownSelector
        appHeaderLogo={appHeaderLogo}
        products={products}
        selectProduct={selectProduct}
        selectedBreakdownProduct={{
          id: showProductBreakdown.id,
          category: showProductBreakdown.category,
        }}
        showBreakdown={setShowProductBreakdown}
      />
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.productDetailsHeader}>
        <Header appHeaderLogo={appHeaderLogo} showLogoOnMobile />
      </Box>
      <Box sx={styles.body}>
        <Box sx={styles.introPanel}>
          {appLogo?.url && (
            <Box>
              <SvgOrImg
                type={appLogo?.type}
                src={appLogo.url}
                alt={appLogo.alt}
                sx={styles.appLogo}
              />
            </Box>
          )}

          <Heading as="h1" sx={styles.heading}>
            {title}
          </Heading>

          <Text as="div" variant="copy1Light" mt="20px">
            {selectProductSubHeading}
          </Text>

          <Button
            iconAfter={<ArrowRightIcon />}
            variant="primary"
            size="large"
            isRound
            sx={styles.chooseButton}
            onClick={() => setShowProductBreakdown(products[0])}
          >
            {t("Choose A Family")}
          </Button>
          {!isRestrictedModeEnabled && (
            <Box sx={styles.recipeFieldWrapper}>
              <Text
                variant="h4Light"
                sx={{ fontSize: "30px", lineHeight: "1" }}
              >
                {t("OR")}
              </Text>

              <Input
                variant="round"
                placeholder={t("ENTER A RECIPE ID...")}
                iconAfter={<ArrowRightIcon />}
                value={recipeID}
                onChange={setRecipeID}
                isInvalid={Boolean(recipeIDError)}
              />

              {recipeIDError && (
                <Text sx={styles.fetchRecipeError}>{recipeIDError}</Text>
              )}
            </Box>
          )}

          <Text variant="copy3Light" mt="30px" mb="18px">
            {selectProductCopy1}
          </Text>

          {selectProductCopy2 && (
            <Text
              variant="copy3Light"
              mb="18px"
              dangerouslySetInnerHTML={{
                __html: t(selectProductCopy2, { link_1 }),
              }}
            />
          )}

          {selectProductDisclaimer && (
            <Text
              variant="copy3Bold"
              dangerouslySetInnerHTML={{
                __html: t(selectProductDisclaimer, { link_1 }),
              }}
            />
          )}
        </Box>

        {!isDesktop && (
          <SvgOrImg
            src={appDivider.url}
            alt={appDivider.alt}
            sx={styles.mobileDivider}
          />
        )}

        <Box sx={styles.buttonsPanel}>
          <Text
            variant="h4Light"
            sx={{ textTransform: "uppercase", lineHeight: "30px" }}
          >
            {selectProductButtonsHeader}
          </Text>

          <Text variant="copy1Light" mt="20px">
            {selectProductButtonsSubHeader}
          </Text>

          <Box sx={styles.cardsContainer}>
            {products
              .filter(({ id }) => !selectedProduct || id === selectedProduct.id)
              .map(({ id, category: { productButtonProps } }) => {
                return (
                  <Box key={id} sx={styles.card}>
                    <ProductButtonV2
                      isSelected={id === selectedProduct?.id}
                      onClick={() => selectProduct(id)}
                      {...productButtonProps}
                    />
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
